import { useState } from 'react';
import { isEmpty } from 'lodash';
import { useLazyQuery, useQuery } from '@apollo/client';

import { ICompany } from 'interfaces/ICompany';
import {
  COMPANIES_MERGED_QUERY,
  SEARCH_COMPANIES_QUERY,
} from 'graphql/company';

import { CompanyFieldsNames } from './constants';

export interface IFieldsErrors {
  [CompanyFieldsNames.OPERATING_NAME]?: boolean;
  [CompanyFieldsNames.LEGAL_NAME]?: boolean;
}

interface Props {
  company: ICompany;
}
export const useValidateCompany = ({ company }: Props) => {
  const [fieldsErrors, setFieldsErrors] = useState<IFieldsErrors | null>(null);

  const { data: mergedCompanies } = useQuery<{ companiesMerged: ICompany[] }>(
    COMPANIES_MERGED_QUERY,
    {
      variables: {
        companyId: company.id,
      },
      skip: !company.id,
    },
  );

  const [requestMatchingCompanies] = useLazyQuery<{
    companies: { results: ICompany[] };
  }>(SEARCH_COMPANIES_QUERY);

  const validateCompanyName = async (
    validationFields: (keyof IFieldsErrors)[],
  ) => {
    // allows to enter name which exist on alias company names, prohibit company name if exists out of aliases
    const aliasCompanyNames = mergedCompanies?.companiesMerged.map(c => c.name);
    const allFieldsInAlias = validationFields.every(field =>
      aliasCompanyNames?.includes(company[field]),
    );
    if (allFieldsInAlias) return;

    const errors: IFieldsErrors = {};

    for (const field of validationFields) {
      const fieldValue = company[field];

      const { data: companiesData } = await requestMatchingCompanies({
        variables: {
          search: {
            filter: {
              name: fieldValue,
            },
          },
          skip: !fieldValue,
        },
      });

      const matchingCompanies = companiesData?.companies?.results || [];
      const fieldValueLowerCase = fieldValue?.toLowerCase();
      const hasMatch = matchingCompanies?.some(
        (c: ICompany) => c?.name?.toLowerCase() === fieldValueLowerCase,
      );
      if (hasMatch) errors[field] = true;
    }
    setFieldsErrors(errors);
    return errors;
  };

  return {
    variables: {
      hasValidationError: !isEmpty(fieldsErrors),
      fieldsErrors,
    },
    handlers: {
      setFieldsErrors,
      validateCompanyName,
    },
  };
};
